import './DeleteProjectConfirmationModal.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, ReactElement, SyntheticEvent, useState } from 'react';
import { Trans } from 'react-i18next';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { S_Link } from '../../../../design/links/S_Link';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { ValidatedTextInput } from '../../../components/ValidatedTextInput';
import { InputStatus } from '../../../models/error.model';

interface IConfirmationModalProps {
  showModal: boolean;
  projectDatasetsToDelete: string[];
  onConfirm(deleteDatasets: boolean): Promise<void>;
  onCancel(): void;
}

export const DeleteProjectConfirmationModal = observer((props: IConfirmationModalProps): ReactElement => {
  const { t } = useTranslator();
  const [deleteDatasets, setDeleteDatasets] = useState<boolean>(false);
  const [showDatasetList, setShowDatasetList] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [confirmationStatus, setConfirmationStatus] = useState<InputStatus>(InputStatus.empty());

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDeleteDatasets(event.target.checked);
  };

  const onConfirm = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    await props.onConfirm(deleteDatasets);
    resetState();
  };

  const onCancel = (): void => {
    resetState();
    props.onCancel();
  };

  const onConfirmationTextChange = (confirmationText: string): void => {
    setConfirmationText(confirmationText);
    validateConfirmationText(confirmationText);
  };

  const validateConfirmationText = (value: string): boolean => {
    const confirmationStatus = InputStatus.valid();
    confirmationStatus.isValid = t('delete_datasets_confirmation', { count: props.projectDatasetsToDelete.length }) === value;
    setConfirmationStatus(confirmationStatus);
    return confirmationStatus.isValid;
  };

  const showDatasetListHandler = (): void => {
    setShowDatasetList(true);
  };

  const renderDatasetsList = (): ReactElement => {
    const datasetsToDelete = props.projectDatasetsToDelete;

    if (datasetsToDelete.length > 5 && !showDatasetList) {
      return (
        <S_Link
          onClick={showDatasetListHandler}
          color='text'
        >
          {t('delete_datasets_list_show_all')} ({datasetsToDelete.length})
        </S_Link>
      );
    }

    return (
      <>
        {datasetsToDelete.map((d) => (
          <li key={d}>
            <span>{d}</span>
          </li>
        ))}
      </>
    );
  };

  const resetState = (): void => {
    setDeleteDatasets(false);
    setShowDatasetList(false);
    setConfirmationText('');
    setConfirmationStatus(InputStatus.empty());
  };

  const datasetsToDeleteCount = props.projectDatasetsToDelete.length;
  const isConfirmDisabled = deleteDatasets && !confirmationStatus.isValid;
  const isDeleteDatasetsDisabled = props.projectDatasetsToDelete.length === 0;

  return (
    <S_Modal
      isOpen={props.showModal}
      toggle={onCancel}
      headerText={t('delete_confirmation_project_header')}
      className='delete-project-confirmation-modal'
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>
          <ButtonWithLoader
            onClick={onConfirm}
            disabled={isConfirmDisabled}
            loaderKey='delete-project-button'
          >
            {t('delete')}
          </ButtonWithLoader>
        </>
      }
    >
      <ColumnLayout size='md'>
        <div>{t('delete_confirmation_project_message')}</div>
        <div title={isDeleteDatasetsDisabled ? t('delete_datasets_disabled') : ''}>
          <S_Checkbox
            checked={deleteDatasets}
            onChange={onChange}
            id='cascade-dataset-delete'
            label={t('delete_datasets')}
            disabled={isDeleteDatasetsDisabled}
          />
        </div>
        <div hidden={!deleteDatasets}>
          <ColumnLayout size='sm'>
            <div className='alerts'>
              <div className='alerts-yellow'>
                <FontAwesomeIcon icon='exclamation-triangle' />
                {t('delete_datasets_info')}:
              </div>
            </div>
            <ul className='datasets-list'>{renderDatasetsList()}</ul>
          </ColumnLayout>
          <div>
            <br />
            <p>
              <Trans
                i18nKey='delete_datasets_confirmation_header'
                count={datasetsToDeleteCount}
                values={{ count: datasetsToDeleteCount }}
                components={{ b: <b /> }}
                t={t as TFunction}
              />
            </p>
            <ValidatedTextInput
              id='confirmation-input'
              type='text'
              maxLength={200}
              value={confirmationText}
              placeholder={t('delete_datasets_confirmation', { count: datasetsToDeleteCount })}
              onChange={onConfirmationTextChange}
              isValid={confirmationStatus.isValid}
              feedbacks={confirmationStatus.errorCodes}
            />
          </div>
        </div>
      </ColumnLayout>
    </S_Modal>
  );
});
